/**
 * Folder Structure Builder
 * Builds hierarchical structures for folder-based exports
 */

import { ExportTemplate, NamingConvention, TemplateField } from '../../../types/exportTemplates';
import { applyNamingConvention } from '../core/naming';
import { extractFields } from '../fieldMapping/extraction';
import { LookupContext } from '../fieldMapping/references';

/**
 * Folder-based nested structure for items
 * Used to build hierarchical exports like Grimoire.scenes.player_house.bed.inspect_text
 */
export interface FolderNode {
  [key: string]: FolderNode | ItemFieldsNode;
}

export interface ItemFieldsNode {
  _isItem: true;
  _itemData: any;
  _fields: TemplateField[];
  [fieldName: string]: any;
}

/**
 * Build a folder-based nested structure from items
 * Items are organized by their folder path, with the item name as the final key
 * 
 * @param items - The items to structure
 * @param template - Export template for field extraction
 * @param blueprints - Available blueprints
 * @param folderPathResolver - Function to resolve folder ID to path string
 * @param lookupContext - Context for resolving reference fields
 * @param namingConventionOverride - Optional override for all naming conventions
 * @returns Nested structure object
 */
export function buildFolderBasedStructure(
  items: any[],
  template: ExportTemplate,
  blueprints: any[],
  folderPathResolver: (folderId: string | null | undefined) => string,
  lookupContext?: LookupContext,
  namingConventionOverride?: NamingConvention,
  useTranslationKeys: boolean = true
): FolderNode {
  const root: FolderNode = {};
  
  // Use override if provided, otherwise use template conventions
  const instanceConvention = namingConventionOverride || template.namingConventions.instanceName;
  const fieldConvention = namingConventionOverride || template.namingConventions.fieldName;
  
  items.forEach(item => {
    // Get folder path for this item
    const folderPath = folderPathResolver(item.folder);
    
    // Extract fields for this item
    const fields = extractFields(item, template, blueprints, undefined, lookupContext, useTranslationKeys);
    
    // Get sanitized item name for the key
    const itemKey = applyNamingConvention(item.name || item.id, instanceConvention);
    
    // Create the item fields node
    const itemNode: ItemFieldsNode = {
      _isItem: true,
      _itemData: item,
      _fields: fields,
    };
    
    // Add field values to the item node
    fields.forEach(field => {
      let fieldName = applyNamingConvention(field.label, fieldConvention);
      
      // Check for field name override (only if not using a global override)
      if (!namingConventionOverride && template.fieldNameOverrides && item.blueprint_id) {
        const blueprintOverrides = template.fieldNameOverrides[item.blueprint_id];
        if (blueprintOverrides && blueprintOverrides[field.label]) {
          const override = blueprintOverrides[field.label];
          if (override.useExportName && override.exportName) {
            fieldName = override.exportName;
          }
        }
      }
      
      itemNode[fieldName] = field.value;
    });
    
    if (!folderPath || folderPath === '') {
      // Item at root level (no folder)
      root[itemKey] = itemNode;
    } else {
      // Parse folder path and create nested structure
      // Path is like "/scenes/player_house" - split and filter empty strings
      const pathParts = folderPath.split('/').filter(part => part.length > 0);
      
      // Navigate/create the nested structure
      let current: FolderNode = root;
      pathParts.forEach((part, index) => {
        const sanitizedPart = applyNamingConvention(part, fieldConvention);
        
        if (!current[sanitizedPart]) {
          current[sanitizedPart] = {};
        }
        
        // Only navigate deeper if this isn't an item node
        const next = current[sanitizedPart];
        if (next && typeof next === 'object' && !('_isItem' in next)) {
          current = next as FolderNode;
        }
      });
      
      // Add the item at the final location
      current[itemKey] = itemNode;
    }
  });
  
  return root;
}
